import React, { useState, useEffect, useRef } from 'react';
import { subscriptionsAPI } from '../services/api';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function Subscriptions() {
  const formRef = useRef(null);
  const [subscriptions, setSubscriptions] = useState([]);
  const [members, setMembers] = useState([]);
  const [memberTotals, setMemberTotals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', amount: '', billing_cycle: 'monthly',
    billing_date: '', category: '', notes: '', is_active: true, assigned_to: '',
    is_auto_draft: false
  });

  useEffect(() => { fetchSubscriptions(); fetchMembers(); fetchTotals(); }, []);

  const fetchSubscriptions = async () => {
    try {
      const response = await subscriptionsAPI.getAll();
      setSubscriptions(response.data);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally { setLoading(false); }
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/household`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMembers(response.data.members || []);
    } catch (error) { console.error('Error fetching members:', error); }
  };

  const fetchTotals = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/subscriptions/totals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMemberTotals(response.data);
    } catch (error) { console.error('Error fetching totals:', error); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...formData, assigned_to: formData.assigned_to || null };
      if (editingId) { await subscriptionsAPI.update(editingId, payload); }
      else { await subscriptionsAPI.create(payload); }
      fetchSubscriptions(); fetchTotals(); resetForm();
    } catch (error) {
      console.error('Error saving subscription:', error);
      alert('Failed to save subscription');
    }
  };

  const handleEdit = (sub) => {
    setFormData({
      name: sub.name, amount: sub.amount, billing_cycle: sub.billing_cycle,
      billing_date: sub.billing_date.split('T')[0], category: sub.category || '',
      notes: sub.notes || '', is_active: sub.is_active,
      assigned_to: sub.assigned_to || '',
      is_auto_draft: !!sub.is_auto_draft
    });
    setEditingId(sub.id);
    setShowForm(true);
    setTimeout(() => {
      if (formRef.current) {
        const top = formRef.current.getBoundingClientRect().top + window.scrollY - 72;
        window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
      }
    }, 50);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this subscription?')) {
      try { await subscriptionsAPI.delete(id); fetchSubscriptions(); fetchTotals(); }
      catch (error) { console.error('Error deleting subscription:', error); }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', amount: '', billing_cycle: 'monthly',
      billing_date: '', category: '', notes: '', is_active: true, assigned_to: '', is_auto_draft: false });
    setEditingId(null);
    setShowForm(false);
  };

  const visibleSubs = activeTab === 'all'
    ? subscriptions
    : subscriptions.filter(s => String(s.assigned_to) === activeTab);

  const calculateMonthlyTotal = () => {
    return visibleSubs.reduce((total, sub) => {
      if (!sub.is_active) return total;
      let monthlyAmount = parseFloat(sub.amount);
      switch (sub.billing_cycle) {
        case 'weekly': monthlyAmount *= 4.33; break;
        case 'biweekly': monthlyAmount *= 2.17; break;
        case 'quarterly': monthlyAmount /= 3; break;
        case 'biannual': monthlyAmount /= 6; break;
        case 'yearly': monthlyAmount /= 12; break;
        default: break;
      }
      return total + monthlyAmount;
    }, 0);
  };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>Subscriptions</h2>
          <p>Manage your recurring services and memberships</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
          {showForm ? 'Cancel' : '+ Add Subscription'}
        </button>
      </div>

      {/* Person filter tabs */}
      {members.length > 0 && (
        <div style={{
          display: 'flex', gap: '8px', marginBottom: '16px',
          borderBottom: '1px solid var(--border-color)',
          overflowX: 'auto', flexWrap: 'nowrap'
        }}>
          <button
            onClick={() => setActiveTab('all')}
            style={{
              padding: '8px 18px', fontSize: '14px', fontWeight: 600,
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: activeTab === 'all' ? '2px solid var(--accent-coral)' : '2px solid transparent',
              color: activeTab === 'all' ? 'var(--accent-coral)' : 'var(--text-muted)',
              whiteSpace: 'nowrap', transition: 'color 0.15s'
            }}
          >All</button>
          {members.map(member => (
            <button
              key={member.id}
              onClick={() => setActiveTab(String(member.id))}
              style={{
                padding: '8px 18px', fontSize: '14px', fontWeight: 600,
                background: 'none', border: 'none', cursor: 'pointer',
                borderBottom: activeTab === String(member.id) ? '2px solid var(--accent-coral)' : '2px solid transparent',
                color: activeTab === String(member.id) ? 'var(--accent-coral)' : 'var(--text-muted)',
                whiteSpace: 'nowrap', transition: 'color 0.15s'
              }}
            >
              {member.full_name || member.username}
            </button>
          ))}
        </div>
      )}

      {memberTotals.length > 0 && (
        <div className="card" style={{ marginBottom: '16px' }}>
          <h3 style={{ marginBottom: '12px', fontSize: '14px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subscriptions by Member</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {memberTotals.map(member => (
              <div key={member.user_id} style={{
                flex: '1', minWidth: '140px', padding: '12px 16px',
                background: 'var(--bg-secondary)', borderRadius: '8px',
                borderLeft: '3px solid var(--accent-coral)'
              }}>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '4px' }}>
                  {member.full_name || member.username}
                </div>
                <div style={{ fontSize: '20px', fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: 'var(--accent-coral)' }}>
                  ${parseFloat(member.total_amount).toFixed(2)}
                </div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
                  {member.subscription_count} subscription{member.subscription_count !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card card-table">
        {showForm && (
          <div ref={formRef} className="form-section">
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Service Name *</label>
                  <input type="text" className="form-input" value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})} required
                    placeholder="e.g. Netflix, Spotify" />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount *</label>
                  <input type="number" step="0.01" className="form-input" value={formData.amount}
                    onChange={(e) => setFormData({...formData, amount: e.target.value})} required
                    placeholder="0.00" />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Billing Cycle *</label>
                  <select className="form-select" value={formData.billing_cycle}
                    onChange={(e) => setFormData({...formData, billing_cycle: e.target.value})}>
                    <option value="weekly">Weekly</option>
                    <option value="biweekly">Bi-Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="quarterly">Quarterly</option>
                    <option value="biannual">Bi-Annual</option>
                    <option value="yearly">Yearly</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Next Billing Date *</label>
                  <input type="date" className="form-input" value={formData.billing_date}
                    onChange={(e) => setFormData({...formData, billing_date: e.target.value})} required />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input type="text" className="form-input" placeholder="e.g. Entertainment, Software"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Assign To</label>
                  <select className="form-select" value={formData.assigned_to}
                    onChange={(e) => setFormData({...formData, assigned_to: e.target.value})}>
                    <option value="">Unassigned</option>
                    {members.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.full_name || member.username}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-textarea" value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  placeholder="Optional notes about this subscription..." />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" className="checkbox" checked={formData.is_auto_draft}
                      onChange={(e) => setFormData({...formData, is_auto_draft: e.target.checked})} />
                    Auto-Drafted
                  </label>
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '16px' }}>
                  <div className="flex gap-1">
                    <button type="submit" className="btn btn-success">
                      {editingId ? 'Update' : 'Add'}
                    </button>
                    <button type="button" onClick={resetForm} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="total-display">
          <span className="total-label">Monthly Total:</span>
          <span className="total-amount coral">${calculateMonthlyTotal().toFixed(2)}</span>
        </div>

        {visibleSubs.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">💳</div>
            <p className="empty-state-text">{subscriptions.length === 0 ? 'No subscriptions yet' : 'No subscriptions for this person'}</p>
            {subscriptions.length === 0 && (
              <button onClick={() => setShowForm(true)} className="btn btn-primary">
                Add Your First Subscription
              </button>
            )}
          </div>
        ) : (
          <div className="tracker-table-wrap">
            <table className="tracker-table">
              <thead>
                <tr>
                  <th className="tracker-th col-name">Name</th>
                  <th className="tracker-th col-amount">Amount</th>
                  <th className="tracker-th col-due">Next Billing</th>
                  <th className="tracker-th col-category">Category</th>
                  <th className="tracker-th col-cycle">Billing Cycle</th>
                  <th className="tracker-th col-assigned">Assigned To</th>
                  <th className="tracker-th col-status">Status</th>
                  <th className="tracker-th col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {visibleSubs.map((sub) => (
                  <tr key={sub.id} className={`tracker-row${!sub.is_active ? ' tracker-row-paid' : ''}`}>
                    <td className="tracker-td col-name tracker-name">
                      <div>{sub.name}</div>
                      {!!sub.is_auto_draft && (
                        <span style={{
                          display: 'inline-block', marginTop: 3, fontSize: 10, fontWeight: 700,
                          padding: '1px 6px', borderRadius: 4,
                          background: 'var(--accent-teal-soft)', color: 'var(--accent-teal)',
                          border: '1px solid rgba(78,205,196,0.25)', textTransform: 'uppercase', letterSpacing: '0.06em'
                        }}>Auto Draft</span>
                      )}
                      {sub.notes && (
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2, fontWeight: 400 }}>{sub.notes}</div>
                      )}
                    </td>
                    <td className="tracker-td col-amount tracker-mono">
                      ${parseFloat(sub.amount).toFixed(2)}
                    </td>
                    <td className="tracker-td col-due">
                      {new Date(sub.billing_date).toLocaleDateString(undefined, { timeZone: 'UTC' })}
                    </td>
                    <td className="tracker-td col-category">
                      {sub.category
                        ? <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>{sub.category}</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
                    </td>
                    <td className="tracker-td col-cycle">
                      <span className="badge badge-primary">{sub.billing_cycle}</span>
                    </td>
                    <td className="tracker-td col-assigned">
                      {sub.assigned_to_name
                        ? <span className="badge badge-secondary">{sub.assigned_to_name}</span>
                        : <span style={{ color: 'var(--text-muted)', fontSize: '12px' }}>—</span>}
                    </td>
                    <td className="tracker-td col-status">
                      {sub.is_active
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge" style={{ background: 'rgba(100,100,100,0.15)', color: 'var(--text-muted)', border: '1px solid rgba(100,100,100,0.2)' }}>Inactive</span>}
                    </td>
                    <td className="tracker-td col-actions">
                      <div className="list-item-actions">
                        <button onClick={() => handleEdit(sub)} className="btn btn-secondary btn-sm">Edit</button>
                        <button onClick={() => handleDelete(sub.id)} className="btn btn-danger btn-sm">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Subscriptions;
